import { AlertsConstants } from './types';
import { NetworkService } from '../../services';
import { config } from '../../config';

// API endpoints

const {
  BASE_URL,
  BUILDING_USERS_URL,
  ALERTS_ALERTS_URL,
  ALERTS_V2_URL,
  ALERTS_V2_INCIDENTS_URL,
  TRACKING_URL,
} = config;

export class AlertsActions {
  static loadAllAlerts(building) {
    return async (dispatch) => {
      dispatch({ type: AlertsConstants.StartLoading });

      const callback = () => {
        const requestOptions = NetworkService.getRequestOptions('GET');
        const API_ENDPOINT = `${BASE_URL}${ALERTS_ALERTS_URL}${building}/`;
        return fetch(API_ENDPOINT, requestOptions);
      };

      const response = await NetworkService.fetchWithRetry(dispatch, callback);
      if (!response.ok || !response || response === 'error') {
        const error = 'There was an error loading building alerts.';
        return dispatch(AlertsActions.setAlertsError(error));
      }

      const data = await response.json();
      await dispatch({ type: AlertsConstants.FinishLoading });
      return dispatch({ type: AlertsConstants.LoadAllAlerts, payload: data });
    };
  }

  static loadAllAlertsV2(building, pageSize = '', pageNum = '') {
    return async (dispatch) => {
      dispatch({ type: AlertsConstants.StartLoading });

      const callback = () => {
        const requestOptions = NetworkService.getRequestOptions('GET');
        let API_ENDPOINT = `${BASE_URL}${ALERTS_V2_URL}${building}/`;
        if (pageSize) {
          if (pageNum) {
            API_ENDPOINT = `${API_ENDPOINT}?page_size=${pageSize}&page=${pageNum}`;
          } else {
            API_ENDPOINT = `${API_ENDPOINT}?page_size=${pageSize}`;
          }
        }
        return fetch(API_ENDPOINT, requestOptions);
      };

      const response = await NetworkService.fetchWithRetry(dispatch, callback);
      if (!response.ok || !response || response === 'error') {
        const error = 'There was an error loading building alerts.';
        return dispatch(AlertsActions.setAlertsError(error));
      }

      const data = await response.json();
      await dispatch({ type: AlertsConstants.FinishLoading });
      return dispatch({ type: AlertsConstants.LoadAllAlerts, payload: data });
    };
  }

  static loadAllIncidents(building, pageSize = '', pageNum = '', showResolved) {
    return async (dispatch) => {
      dispatch({ type: AlertsConstants.StartLoading });

      const callback = () => {
        const requestOptions = NetworkService.getRequestOptions('GET');
        const API_ENDPOINT = `${BASE_URL}${ALERTS_V2_INCIDENTS_URL}${building}/?page_size=${pageSize}&page=${pageNum}&is_resolved=${showResolved}`;

        return fetch(API_ENDPOINT, requestOptions);
      };

      const response = await NetworkService.fetchWithRetry(dispatch, callback);
      if (!response.ok || !response || response === 'error') {
        const error = 'There was an error loading alert incidents.';
        return dispatch(AlertsActions.setAlertsError(error));
      }

      const data = await response.json();
      await dispatch({ type: AlertsConstants.LoadAllIncidents, payload: data });
      return dispatch({ type: AlertsConstants.FinishLoading });
    };
  }

  static fetchAlert(building, alertId) {
    return async (dispatch) => {
      dispatch({ type: AlertsConstants.StartLoading });
      const callback = () => {
        const requestOptions = NetworkService.getRequestOptions('GET');
        const API_ENDPOINT = `${BASE_URL}${ALERTS_V2_URL}${building}/${alertId}/`;

        return fetch(API_ENDPOINT, requestOptions);
      };

      const response = await NetworkService.fetchWithRetry(dispatch, callback);
      if (!response.ok || !response || response === 'error') {
        const error = 'There was an error loading alert incidents.';
        return dispatch(AlertsActions.setAlertsError(error));
      }

      const data = await response.json();
      await dispatch({ type: AlertsConstants.FinishLoading });
      return data;
    };
  }

  static getIncidentDetails(building, incident) {
    return async (dispatch) => {
      dispatch({ type: AlertsConstants.StartLoadingIncident });

      const callback = () => {
        const requestOptions = NetworkService.getRequestOptions('GET');
        const API_ENDPOINT = `${BASE_URL}/alerts/api/incidents/${building}/${incident}/`;

        return fetch(API_ENDPOINT, requestOptions);
      };

      const response = await NetworkService.fetchWithRetry(dispatch, callback);
      if (!response.ok || !response || response === 'error') {
        const error = 'There was an error retrieving incident details.';
        return dispatch(AlertsActions.setAlertsError(error));
      }

      const data = await response.json();
      await dispatch({ type: AlertsConstants.SetSelectedIncident, payload: data });
      return dispatch({ type: AlertsConstants.FinishLoadingIncident });
    };
  }

  static getInformationGraphData(dataApiLocation) {
    return async (dispatch) => {
      const callback = () => {
        const requestOptions = NetworkService.getRequestOptions('GET');
        const API_ENDPOINT = `${BASE_URL}${dataApiLocation}`;

        return fetch(API_ENDPOINT, requestOptions);
      };

      const response = await NetworkService.fetchWithRetry(dispatch, callback);
      if (!response.ok || !response || response === 'error') {
        const error = 'There was an error retrieving graph data.';
        return dispatch(AlertsActions.setAlertsError(error));
      }

      return response.json();
    };
  }

  static clearCreateAlertConditionSuccess() {
    return { type: AlertsConstants.ClearCreateAlertConditionSuccess };
  }

  static clearEditAlertConditionSuccess() {
    return { type: AlertsConstants.ClearEditAlertConditionSuccess };
  }

  static createAlert(
    building,
    name,
    message,
    description,
    alertType,
    severityLevel,
    isActive,
    startDate,
    endDate,
    threshold,
    autoResolveThreshold,
    conditions,
    subscribers,
    contexts,
  ) {
    return async (dispatch) => {
      dispatch({ type: AlertsConstants.StartLoading });

      const callback = () => {
        const body = JSON.stringify({
          name,
          message,
          conditions: conditions,
          subscribers: subscribers,
          contexts: contexts,
          description,
          alert_type: alertType,
          severity_level: severityLevel,
          threshold,
          auto_resolve_threshold: autoResolveThreshold,
          schedule_start_date: startDate,
          schedule_end_date: endDate,
          is_active: isActive,
        });

        const requestOptions = NetworkService.getRequestOptions('POST', body);
        const API_ENDPOINT = `${BASE_URL}${ALERTS_V2_URL}${building}/`;

        return fetch(API_ENDPOINT, requestOptions);
      };

      const response = await NetworkService.fetchWithRetry(dispatch, callback);
      if (!response.ok || !response || response === 'error') {
        return dispatch(AlertsActions.setAlertsError(response.json().message));
      }
      const data = await response.json();

      await dispatch({ type: AlertsConstants.CreateAlertConditionSuccess });
      await dispatch({ type: AlertsConstants.FinishLoading });
      return data.uid_hash;
    };
  }

  static updateAlert(
    alert_uid_hash,
    building,
    name,
    message,
    description,
    alertType,
    severityLevel,
    isActive,
    startDate,
    endDate,
    threshold,
    autoResolveThreshold,
  ) {
    return async (dispatch) => {
      dispatch({ type: AlertsConstants.StartLoading });

      const callback = () => {
        const body = JSON.stringify({
          name,
          message,
          description,
          alert_type: alertType,
          severity_level: severityLevel,
          threshold,
          auto_resolve_threshold: autoResolveThreshold,
          schedule_start_date: startDate,
          schedule_end_date: endDate,
          is_active: isActive,
        });
        const requestOptions = NetworkService.getRequestOptions('PATCH', body);
        const API_ENDPOINT = `${BASE_URL}${ALERTS_V2_URL}${building}/${alert_uid_hash}/`;
        return fetch(API_ENDPOINT, requestOptions);
      };

      const response = await NetworkService.fetchWithRetry(dispatch, callback);
      if (!response.ok || !response || response === 'error') {
        return dispatch(AlertsActions.setAlertsError(response.json().message));
      }

      await dispatch({ type: AlertsConstants.EditAlertConditionSuccess });
      return dispatch({ type: AlertsConstants.FinishLoading });
    };
  }

  static getRegisterSlugs(building) {
    return async (dispatch) => {
      const callback = () => {
        const requestOptions = NetworkService.getRequestOptions('GET');
        const API_ENDPOINT = `${BASE_URL}${TRACKING_URL}${building}/alert-autocomplete/`;

        return fetch(API_ENDPOINT, requestOptions);
      };

      const response = await NetworkService.fetchWithRetry(dispatch, callback);
      if (!response.ok || !response || response === 'error') {
        const error = 'There was an error retrieving register slugs.';
        return dispatch(AlertsActions.setAlertsError(error));
      }

      const data = await response.json();
      return dispatch({ type: AlertsConstants.SetRegisterSlugs, payload: data });
    };
  }

  static createNewCondition(endpoint, conditionObj) {
    return async (dispatch) => {
      const callback = () => {
        const body = JSON.stringify(conditionObj);
        const requestOptions = NetworkService.getRequestOptions('POST', body);
        const API_ENDPOINT = `${BASE_URL}${endpoint}`;

        return fetch(API_ENDPOINT, requestOptions);
      };

      const response = await NetworkService.fetchWithRetry(dispatch, callback);
      if (!response.ok || !response || response === 'error') {
        return null;
      }

      return dispatch({ type: AlertsConstants.EditAlertConditionSuccess });
    };
  }

  static updateCondition(endpoint, name, value) {
    return async (dispatch) => {
      const callback = () => {
        const body = JSON.stringify({
          [name]: value,
        });
        const requestOptions = NetworkService.getRequestOptions('PATCH', body);
        const API_ENDPOINT = `${BASE_URL}${endpoint}`;

        return fetch(API_ENDPOINT, requestOptions);
      };

      const response = await NetworkService.fetchWithRetry(dispatch, callback);
      if (!response.ok || !response || response === 'error') {
        return null;
      }

      return dispatch({ type: AlertsConstants.EditAlertConditionSuccess });
    };
  }

  static removeCondition(endpoint) {
    return async (dispatch) => {
      const callback = () => {
        const requestOptions = NetworkService.getRequestOptions('DELETE');
        const API_ENDPOINT = `${BASE_URL}${endpoint}`;

        return fetch(API_ENDPOINT, requestOptions);
      };

      const response = await NetworkService.fetchWithRetry(dispatch, callback);
      if (!response.ok || !response || response === 'error') {
        return null;
      }

      return dispatch({ type: AlertsConstants.EditAlertConditionSuccess });
    };
  }

  static getSubscribers(building) {
    return async (dispatch) => {
      const callback = () => {
        const requestOptions = NetworkService.getRequestOptions('GET');
        const API_ENDPOINT = `${BASE_URL}${BUILDING_USERS_URL}autocomplete/${building}/`;

        return fetch(API_ENDPOINT, requestOptions);
      };

      const response = await NetworkService.fetchWithRetry(dispatch, callback);
      if (!response.ok || !response || response === 'error') {
        const error = 'There was an error retrieving subscribers.';
        return dispatch(AlertsActions.setAlertsError(error));
      }

      const data = await response.json();
      return dispatch({ type: AlertsConstants.SetSubscribers, payload: data });
    };
  }

  static updateSubscribers(endpoint, subscriber, operation) {
    return async (dispatch) => {
      const callback = () => {
        subscriber.operation = operation;
        const body = JSON.stringify(subscriber);
        const requestOptions = NetworkService.getRequestOptions('PATCH', body);
        const API_ENDPOINT = `${BASE_URL}${endpoint}`;

        return fetch(API_ENDPOINT, requestOptions);
      };

      const response = await NetworkService.fetchWithRetry(dispatch, callback);
      if (!response.ok || !response || response === 'error') {
        const error = 'There was an error retrieving subscribers.';
        return dispatch(AlertsActions.setAlertsError(error));
      }
      await dispatch({ type: AlertsConstants.EditAlertConditionSuccess });
      return dispatch({ type: AlertsConstants.FinishLoading });
    };
  }

  static createNewContext(endpoint, context) {
    return async (dispatch) => {
      const callback = () => {
        const body = JSON.stringify({
          data_point_tag: context,
        });
        const requestOptions = NetworkService.getRequestOptions('POST', body);
        const API_ENDPOINT = `${BASE_URL}${endpoint}`;

        return fetch(API_ENDPOINT, requestOptions);
      };

      const response = await NetworkService.fetchWithRetry(dispatch, callback);
      if (!response.ok || !response || response === 'error') {
        return null;
      }

      return dispatch({ type: AlertsConstants.EditAlertConditionSuccess });
    };
  }

  static updateContext(endpoint, context) {
    return async (dispatch) => {
      const callback = () => {
        const body = JSON.stringify({
          data_point_tag: context,
        });
        const requestOptions = NetworkService.getRequestOptions('PATCH', body);
        const API_ENDPOINT = `${BASE_URL}${endpoint}`;

        return fetch(API_ENDPOINT, requestOptions);
      };

      const response = await NetworkService.fetchWithRetry(dispatch, callback);
      if (!response.ok || !response || response === 'error') {
        return null;
      }

      return dispatch({ type: AlertsConstants.EditAlertConditionSuccess });
    };
  }

  static removeContext(endpoint) {
    return async (dispatch) => {
      const callback = () => {
        const requestOptions = NetworkService.getRequestOptions('DELETE');
        const API_ENDPOINT = `${BASE_URL}${endpoint}`;

        return fetch(API_ENDPOINT, requestOptions);
      };

      const response = await NetworkService.fetchWithRetry(dispatch, callback);
      if (!response.ok || !response || response === 'error') {
        return null;
      }

      return dispatch({ type: AlertsConstants.EditAlertConditionSuccess });
    };
  }

  static addComment(comment, commentApiLocation) {
    return async (dispatch) => {
      const callback = () => {
        const body = JSON.stringify({
          comment,
        });
        const requestOptions = NetworkService.getRequestOptions('POST', body);
        const API_ENDPOINT = `${BASE_URL}${commentApiLocation}`;

        return fetch(API_ENDPOINT, requestOptions);
      };

      const response = await NetworkService.fetchWithRetry(dispatch, callback);
      if (!response.ok || !response || response === 'error') {
        return null;
      }

      return true;
    };
  }

  static updateComment(comment, commentApiLocation) {
    return async (dispatch) => {
      const callback = () => {
        const body = JSON.stringify({
          comment,
        });
        const requestOptions = NetworkService.getRequestOptions('PATCH', body);
        const API_ENDPOINT = `${BASE_URL}${commentApiLocation}`;

        return fetch(API_ENDPOINT, requestOptions);
      };

      const response = await NetworkService.fetchWithRetry(dispatch, callback);
      if (!response.ok || !response || response === 'error') {
        return null;
      }

      return true;
    };
  }

  static resolveIncident(apiLocation, reason) {
    return async (dispatch) => {
      const callback = () => {
        const body = JSON.stringify({
          reason,
        });
        const requestOptions = NetworkService.getRequestOptions('POST', body);
        const API_ENDPOINT = `${BASE_URL}${apiLocation}`;

        return fetch(API_ENDPOINT, requestOptions);
      };

      const response = await NetworkService.fetchWithRetry(dispatch, callback);
      if (!response.ok || !response || response === 'error') {
        return false;
      }

      return true;
    };
  }

  static setAlertsError(error) {
    return { type: AlertsConstants.SetAlertsError, payload: error };
  }

  static clearAlertsError() {
    return { type: AlertsConstants.ClearAlertsError };
  }
}
